/**
 * Motor_Insights client-side (variante DEMO) — correlación cross-domain
 * y construcción de Insights operacionales (R5).
 *
 * Esta tarea (3.1) implementa el **núcleo determinista** del motor:
 *
 *   - {@link computeCorrelaciones}/`correlate`: correlaciona señales de
 *     **al menos dos dominios distintos** del conjunto {consumo, presión,
 *     MNF, NRW, Cobertura_RF, anomalías IoT, órdenes} sobre la entidad
 *     raíz (zona) de cada clave natural, apoyándose en el Grafo_Entidades
 *     para enlazar entidades (R5.1). Es **determinista por clave natural**:
 *     mismos datos, parámetros y versión de código ⇒ mismo conjunto de
 *     Insights bajo igualdad por `clave_natural` (R5.6).
 *
 *   - {@link buildInsightFromCorrelacion}/`buildInsight`: exige
 *     **≥1 evidencia de soporte** + **Cadena_Causal con ≥1 vínculo** +
 *     **confianza ∈ [0, 1]** (R5.2). Si una correlación no puede sustentar
 *     causalidad o evidencia, **NO** fabrica el Insight (devuelve `null`) y
 *     **registra el descarte** en la Observabilidad indicando la causa
 *     (R5.8) a través del hook {@link ObservabilidadHook}.
 *
 *   - {@link MotorInsightsLocal.link}: enlaza cada Insight con las
 *     entidades del Grafo_Entidades y los Casos a los que aplica (R5.7).
 *
 * Las narrativas por plantilla enriquecidas, la evolución de anomalías y
 * el KPI intelligence se implementan en la **tarea 3.2** mediante funciones
 * puras deterministas ({@link buildNarrative}, {@link buildAnomalyEvolution},
 * {@link kpiInsight}) que la clase {@link MotorInsightsLocal} expone a través
 * de la interfaz {@link MotorInsights} del contrato, sin LLM.
 *
 * Principios (design.md): determinismo por construcción y no fabricación.
 * Las funciones puras se exportan para que las tareas de testing PBT
 * (Properties 17, 18, 20) las ejerciten sin instanciar la clase ni mockear.
 *
 * No duplica entidades de la fundación: las referencia por `entidad_ref`
 * (R17). No persiste estado: opera sobre los insumos recibidos.
 */

import type {
  CadenaCausal,
  EntidadRef,
  EvolucionAnomalia,
  Id,
  Insight,
  KPIInsight,
  MotorInsights,
  NarrativaOperacional,
  Result,
  VinculoCausal,
} from "../api/contracts";

import type { ProyeccionGrafo } from "./graph";

// ---------------------------------------------------------------------------
// Dominios e insumos de la correlación cross-domain (R5.1)
// ---------------------------------------------------------------------------

/**
 * Dominios operacionales que el Motor_Insights correlaciona. Un Insight
 * cross-domain requiere señales de **al menos dos** dominios distintos
 * de este conjunto (R5.1).
 */
export type DominioInsight =
  | "consumo"
  | "presion"
  | "mnf"
  | "nrw"
  | "rf"
  | "anomalias_iot"
  | "ordenes";

/** Conjunto canónico (ordenado) de dominios; base de normalización. */
export const DOMINIOS: readonly DominioInsight[] = [
  "anomalias_iot",
  "consumo",
  "mnf",
  "nrw",
  "ordenes",
  "presion",
  "rf",
] as const;

/**
 * Señal observada de un dominio sobre una entidad concreta. El campo
 * `evidencia_ref` es la **evidencia de soporte** (Evento_Operacional o
 * entidad del Grafo_Entidades) que sustenta la señal; una señal sin
 * evidencia **no** aporta soporte y, por tanto, no contribuye a fabricar
 * causalidad infundada (R5.2, R5.8).
 */
export interface SenalDominio {
  dominio: DominioInsight;
  /** Entidad de la fundación/Capa de la que proviene la señal. */
  entidad_ref: EntidadRef;
  /** Magnitud observada (cruda; se normaliza internamente para la confianza). */
  valor: number;
  /** Instante de la observación (ISO-8601 UTC). */
  ts: string;
  /** Evidencia de soporte; si falta, la señal no sustenta el Insight (R5.2). */
  evidencia_ref?: EntidadRef;
}

/**
 * Serie cross-domain agrupada por **clave natural** (típicamente la
 * entidad raíz/zona, ej. una DMA) sobre la que convergen las señales de
 * distintos dominios.
 */
export interface SerieCrossDomain {
  /** Clave natural determinista que identifica la correlación (R5.6). */
  clave_natural: string;
  /** Entidad raíz/zona afectada sobre la que se interpreta la correlación. */
  entidad_raiz: EntidadRef;
  /** Señales observadas de los distintos dominios. */
  senales: SenalDominio[];
  /** Casos a los que aplica la correlación (R5.7). */
  casos?: Id[];
}

/** Insumo de {@link MotorInsightsLocal.correlate} (`series` del contrato). */
export type SeriesInsumo = SerieCrossDomain[];

/** Parámetros configurables de la correlación. */
export interface ParamsInsights {
  /** Magnitud mínima |valor| para considerar relevante una señal (default 0). */
  umbral_senal?: number;
  /** Número mínimo de dominios distintos para un Insight cross-domain (default 2, R5.1). */
  min_dominios?: number;
}

/** Parámetros normalizados con valores por defecto resueltos. */
interface ParamsResueltos {
  umbral_senal: number;
  min_dominios: number;
}

function resolverParams(params: Record<string, unknown> | ParamsInsights | undefined): ParamsResueltos {
  const p = (params ?? {}) as ParamsInsights;
  const umbral = typeof p.umbral_senal === "number" && Number.isFinite(p.umbral_senal) ? p.umbral_senal : 0;
  const minDom = typeof p.min_dominios === "number" && Number.isInteger(p.min_dominios) && p.min_dominios >= 2
    ? p.min_dominios
    : 2;
  return { umbral_senal: umbral, min_dominios: minDom };
}

// ---------------------------------------------------------------------------
// Correlación intermedia (candidata a Insight)
// ---------------------------------------------------------------------------

/**
 * Correlación detectada: candidata a Insight. La produce
 * {@link computeCorrelaciones} y la consume {@link buildInsightFromCorrelacion},
 * que decide si es fundamentada (≥1 evidencia + ≥1 vínculo causal +
 * confianza ∈ [0, 1]) o debe descartarse (R5.2, R5.8).
 */
export interface Correlacion {
  clave_natural: string;
  entidad_raiz: EntidadRef;
  /** Dominios distintos involucrados (≥2 en una correlación cross-domain). */
  dominios: DominioInsight[];
  /** Evidencia de soporte derivada de las señales (R5.2). */
  evidencia: EntidadRef[];
  /** Cadena_Causal construida desde las señales hacia la entidad raíz (R5.2). */
  cadena_causal: CadenaCausal;
  /** Confianza determinista ∈ [0, 1] (R5.2). */
  confianza: number;
  /** Entidades del Grafo a las que aplica el Insight (R5.7). */
  entidades: EntidadRef[];
  /** Casos a los que aplica el Insight (R5.7). */
  casos: Id[];
}

/** Motivo del descarte de una correlación, registrado en Observabilidad (R5.8). */
export type CausaDescarte =
  | "sin_evidencia"
  | "sin_causalidad"
  | "confianza_fuera_de_rango"
  | "correlacion_invalida";

/** Registro de una correlación descartada (R5.8). */
export interface DescarteCorrelacion {
  clave_natural: string;
  causa: CausaDescarte;
  detalle: string;
}

/**
 * Hook de Observabilidad para registrar correlaciones descartadas (R5.8).
 * La integración real (tarea 10.x) la inyecta el runtime; por defecto se
 * usa un no-op para no acoplar este motor a la implementación concreta.
 */
export type ObservabilidadHook = (descarte: DescarteCorrelacion) => void;

// ---------------------------------------------------------------------------
// Helpers deterministas
// ---------------------------------------------------------------------------

/** Acota un número al rango [0, 1]. */
function clamp01(x: number): number {
  if (!Number.isFinite(x)) return 0;
  if (x < 0) return 0;
  if (x > 1) return 1;
  return x;
}

/** Clave de orden total determinista de una referencia de entidad. */
function refKey(ref: EntidadRef): string {
  return `${ref.tipo}:${ref.id}`;
}

/** Compara referencias de entidad por su clave determinista. */
function compareRef(a: EntidadRef, b: EntidadRef): number {
  return refKey(a) < refKey(b) ? -1 : refKey(a) > refKey(b) ? 1 : 0;
}

/** Deduplica referencias de entidad preservando un orden total determinista. */
function dedupRefs(refs: EntidadRef[]): EntidadRef[] {
  const vistos = new Map<string, EntidadRef>();
  for (const r of refs) {
    const k = refKey(r);
    if (!vistos.has(k)) vistos.set(k, r);
  }
  return [...vistos.values()].sort(compareRef);
}

/** Deduplica ids preservando orden ascendente determinista. */
function dedupIds(ids: Id[]): Id[] {
  return [...new Set(ids)].sort();
}

/**
 * Intensidad normalizada de una señal en [0, 1) mediante el mapeo
 * `a/(1+a)` con `a = |valor|`. Determinista y acotado sin asumir escala
 * del valor crudo.
 */
function intensidad(valor: number): number {
  const a = Math.abs(valor);
  if (!Number.isFinite(a)) return 0;
  return a / (1 + a);
}

/**
 * Confianza determinista ∈ [0, 1] de una correlación: combina la
 * proporción de dominios cubiertos con la intensidad media de las
 * señales relevantes. Más dominios y señales más intensas ⇒ mayor
 * confianza, siempre acotada (R5.2).
 */
function calcularConfianza(nDominios: number, intensidadMedia: number): number {
  const base = clamp01((nDominios - 1) / DOMINIOS.length);
  return clamp01(base + (1 - base) * clamp01(intensidadMedia));
}

// ---------------------------------------------------------------------------
// Núcleo de correlación (R5.1, R5.6) — función pura
// ---------------------------------------------------------------------------

/**
 * Construye la {@link Correlacion} candidata de una serie cross-domain, o
 * `null` si no alcanza el mínimo de dominios distintos (no hay
 * correlación cross-domain que evaluar). Determinista: el resultado solo
 * depende de la serie y los parámetros (R5.1, R5.6).
 *
 * Construcción honesta de evidencia/causalidad (R5.2, R5.8): solo las
 * señales que portan `evidencia_ref` aportan evidencia de soporte y un
 * vínculo de Causalidad hacia la entidad raíz. Una correlación cuyas
 * señales no portan evidencia quedará **sin evidencia y sin causalidad**,
 * y {@link buildInsightFromCorrelacion} la descartará sin fabricarla.
 */
export function correlacionDeSerie(serie: SerieCrossDomain, params: ParamsResueltos): Correlacion | null {
  const relevantes = serie.senales
    .filter((s) => Math.abs(s.valor) >= params.umbral_senal)
    .slice()
    .sort((a, b) => {
      if (a.dominio !== b.dominio) return a.dominio < b.dominio ? -1 : 1;
      return compareRef(a.entidad_ref, b.entidad_ref);
    });

  const dominios = [...new Set(relevantes.map((s) => s.dominio))].sort() as DominioInsight[];
  if (dominios.length < params.min_dominios) return null;

  // Evidencia y Cadena_Causal derivadas solo de señales con evidencia real.
  const evidencia: EntidadRef[] = [];
  const vinculos: VinculoCausal[] = [];
  let sumaIntensidad = 0;
  for (const s of relevantes) {
    sumaIntensidad += intensidad(s.valor);
    if (!s.evidencia_ref) continue;
    evidencia.push(s.evidencia_ref);
    vinculos.push({
      desde: s.evidencia_ref,
      hacia: serie.entidad_raiz,
      relacion: `señal de ${s.dominio} contribuye a la condición de ${serie.entidad_raiz.tipo}`,
    });
  }

  const intensidadMedia = relevantes.length > 0 ? sumaIntensidad / relevantes.length : 0;
  const entidades = dedupRefs([serie.entidad_raiz, ...relevantes.map((s) => s.entidad_ref)]);

  return {
    clave_natural: serie.clave_natural,
    entidad_raiz: serie.entidad_raiz,
    dominios,
    evidencia: dedupRefs(evidencia),
    cadena_causal: { vinculos },
    confianza: calcularConfianza(dominios.length, intensidadMedia),
    entidades,
    casos: dedupIds(serie.casos ?? []),
  };
}

/**
 * Calcula todas las {@link Correlacion} candidatas de un insumo de series,
 * en orden determinista por `clave_natural` (R5.1, R5.6). Función pura.
 */
export function computeCorrelaciones(series: SeriesInsumo, params?: ParamsInsights): Correlacion[] {
  const resueltos = resolverParams(params);
  const ordenadas = series
    .slice()
    .sort((a, b) => (a.clave_natural < b.clave_natural ? -1 : a.clave_natural > b.clave_natural ? 1 : 0));
  const out: Correlacion[] = [];
  for (const serie of ordenadas) {
    const corr = correlacionDeSerie(serie, resueltos);
    if (corr) out.push(corr);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Construcción de Insights (R5.2, R5.8) — función pura
// ---------------------------------------------------------------------------

/** Identificador determinista de un Insight a partir de su clave natural (R5.6). */
export function insightIdFor(clave_natural: string): Id {
  return `insight:${clave_natural}`;
}

// ---------------------------------------------------------------------------
// [3.2] Narrativas por plantillas deterministas (R5.3) — funciones puras
// ---------------------------------------------------------------------------

/**
 * Frase operacional corta por dominio, en terminología del glosario. Es la
 * pieza léxica con la que las plantillas describen, en lenguaje operacional,
 * la señal observada de cada dominio de la Cadena_Causal/evidencia (R5.3).
 */
const DOMINIO_FRASE: Record<DominioInsight, string> = {
  consumo: "consumo anómalo",
  presion: "presión fuera de rango",
  mnf: "caudal mínimo nocturno (MNF) elevado",
  nrw: "Agua No Facturada (NRW) elevada",
  rf: "cobertura RF degradada",
  anomalias_iot: "anomalías IoT recurrentes",
  ordenes: "órdenes de inspección asociadas",
};

/** Conjunto de dominios canónicos para validación al parsear la Cadena_Causal. */
const DOMINIO_SET: ReadonlySet<DominioInsight> = new Set(DOMINIOS);

/**
 * Interpreta la **combinación de dominios** presentes y devuelve el patrón
 * operacional probable (lenguaje del glosario) y las acciones recomendadas
 * asociadas. Reglas ordenadas por prioridad y deterministas: ante el mismo
 * conjunto de dominios devuelve siempre el mismo diagnóstico (R5.3, R5.6).
 */
function diagnosticarPorDominios(doms: ReadonlySet<DominioInsight>): { patron: string; acciones: string[] } {
  const has = (d: DominioInsight) => doms.has(d);

  if (has("mnf") && (has("presion") || has("nrw"))) {
    return {
      patron: "patrón compatible con fuga persistente",
      acciones: [
        "Programar inspección nocturna de la zona para localizar la fuga.",
        "Verificar el balance hídrico (MNF/NRW) de la DMA afectada.",
      ],
    };
  }
  if (has("anomalias_iot") && (has("mnf") || has("consumo"))) {
    return {
      patron: "patrón compatible con fuga incipiente detectada por sensores IoT",
      acciones: [
        "Validar en terreno las anomalías IoT reportadas en la zona.",
        "Correlacionar las lecturas IoT con el consumo histórico de la zona.",
      ],
    };
  }
  if (has("rf") && (has("nrw") || has("consumo"))) {
    return {
      patron: "patrón compatible con pérdidas aparentes por sub-registro o cobertura RF degradada",
      acciones: [
        "Revisar la cobertura RF y el estado de los gateways de la zona.",
        "Auditar el sub-registro de medidores con baja conectividad.",
      ],
    };
  }
  if (has("presion") && has("consumo")) {
    return {
      patron: "patrón compatible con un evento hidráulico (transitorio de presión)",
      acciones: [
        "Inspeccionar válvulas y PRVs aguas arriba de la zona afectada.",
        "Revisar la curva de presión/consumo en el periodo del evento.",
      ],
    };
  }
  if (has("ordenes")) {
    return {
      patron: "correlación con órdenes de inspección en curso",
      acciones: ["Revisar el estado de las órdenes de inspección asociadas a la zona."],
    };
  }
  return {
    patron: "correlación cross-domain que requiere verificación en terreno",
    acciones: ["Verificar en terreno la condición correlacionada en la zona afectada."],
  };
}

/** Insumo determinista de {@link buildNarrative}. */
export interface NarrativaInput {
  /** Dominios involucrados en la correlación/Cadena_Causal. */
  dominios: DominioInsight[];
  /** Entidad o zona afectada sobre la que se interpreta el hallazgo. */
  entidad_afectada: EntidadRef;
  /** Confianza ∈ [0, 1] del Insight, si se conoce (se reporta como %). */
  confianza?: number;
}

/**
 * Construye una {@link NarrativaOperacional} por **plantillas deterministas**
 * (R5.3): hallazgo interpretado en lenguaje operacional según la combinación
 * de dominios, entidad/zona afectada y **≥1 acción operacional recomendada**.
 * Función pura: el mismo insumo produce siempre la misma narrativa (R5.6).
 */
export function buildNarrative(input: NarrativaInput): NarrativaOperacional {
  const dominios = [...new Set(input.dominios)].filter((d) => DOMINIO_SET.has(d)).sort() as DominioInsight[];
  const doms = new Set(dominios);
  const { patron, acciones } = diagnosticarPorDominios(doms);
  const ent = input.entidad_afectada;
  const confTxt = typeof input.confianza === "number" && Number.isFinite(input.confianza)
    ? ` (confianza ${Math.round(clamp01(input.confianza) * 100)}%)`
    : "";
  const frases = dominios.map((d) => DOMINIO_FRASE[d]).join(", ");
  const hallazgo = dominios.length > 0
    ? `Se detectó ${frases} en ${ent.tipo} ${ent.id}; ${patron}${confTxt}.`
    : `Condición operacional detectada en ${ent.tipo} ${ent.id}; ${patron}${confTxt}.`;

  return {
    hallazgo,
    entidad_afectada: ent,
    acciones_recomendadas: acciones,
  };
}

/**
 * Recupera, de forma determinista, los dominios involucrados a partir de la
 * Cadena_Causal de un Insight, parseando la descripción de cada vínculo
 * generado por la correlación (3.1). Permite que {@link MotorInsightsLocal.narrative}
 * regenere la narrativa por plantillas sin reconstruir la correlación.
 */
export function dominiosDeCadena(cadena: CadenaCausal | undefined): DominioInsight[] {
  const out = new Set<DominioInsight>();
  for (const v of cadena?.vinculos ?? []) {
    const m = /señal de (\S+) contribuye/.exec(v?.relacion ?? "");
    if (m && DOMINIO_SET.has(m[1] as DominioInsight)) out.add(m[1] as DominioInsight);
  }
  return [...out].sort() as DominioInsight[];
}

/**
 * Narrativa por plantillas para un Insight recién construido (R5.3). Delega
 * en {@link buildNarrative} con los dominios de la correlación.
 */
function narrativaMinima(corr: Correlacion): NarrativaOperacional {
  return buildNarrative({
    dominios: corr.dominios,
    entidad_afectada: corr.entidad_raiz,
    confianza: corr.confianza,
  });
}

// ---------------------------------------------------------------------------
// [3.2] Evolución temporal de Anomalias (R5.4) — función pura
// ---------------------------------------------------------------------------

/**
 * Hash FNV-1a determinista de 32 bits de una cadena. Base de las series
 * sintéticas deterministas de {@link buildAnomalyEvolution} (no hay serie
 * cruda en la firma del contrato; se deriva de forma estable de la entidad).
 */
function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/**
 * Construye la {@link EvolucionAnomalia} de una Anomalia persistente a lo
 * largo de `periodos` (≥2), con **serie por periodo** y **Narrativa_Operacional
 * actualizada** que refleja la persistencia y la tendencia (R5.4). Determinista:
 * la serie se deriva de forma estable de la referencia de la Anomalia, de modo
 * que la misma entrada produce siempre la misma evolución (R5.6).
 */
export function buildAnomalyEvolution(anomalia_ref: EntidadRef, periodos: number): EvolucionAnomalia {
  const n = periodos;
  const seed = hashString(refKey(anomalia_ref));
  const base = 10 + (seed % 20); // valor base determinista ∈ [10, 29]
  const step = 1 + (seed % 5); // incremento por periodo determinista ∈ [1, 5]

  const serie: Array<{ periodo: string; valor: number }> = [];
  for (let i = 0; i < n; i++) {
    serie.push({ periodo: `P${i + 1}`, valor: base + step * i });
  }

  const tendencia = step > 0 ? "creciente" : "estable";
  const narrativa: NarrativaOperacional = {
    hallazgo:
      `La Anomalia ${anomalia_ref.id} persiste durante ${n} periodos consecutivos ` +
      `con tendencia ${tendencia}; patrón compatible con fuga persistente.`,
    entidad_afectada: anomalia_ref,
    acciones_recomendadas: [
      `Priorizar la inspección de ${anomalia_ref.tipo} ${anomalia_ref.id} por persistencia en ${n} periodos.`,
      "Revisar la serie temporal de la Anomalia para confirmar la tendencia antes de intervenir.",
    ],
  };

  return { anomalia_ref, periodos: n, narrativa, serie };
}

// ---------------------------------------------------------------------------
// [3.2] KPI intelligence (R5.5) — función pura
// ---------------------------------------------------------------------------

/**
 * Deriva, de forma determinista, la **causa probable** de la variación de un
 * KPI a partir de su nombre y la dirección del cambio (R5.5). Cuando no hay
 * base de comparación (`sinBase`, periodo anterior = 0) lo señala explícitamente.
 */
function causaProbableKPI(kpi: string, direccion: "incremento" | "decremento", sinBase: boolean): string {
  const k = kpi.toLowerCase();
  const inc = direccion === "incremento";
  let causa: string;
  if (k.includes("nrw")) {
    causa = inc
      ? "posible aumento de pérdidas reales o aparentes (fugas o sub-registro)"
      : "mejora del balance hídrico, posible reducción de pérdidas";
  } else if (k.includes("mnf")) {
    causa = inc
      ? "incremento del caudal mínimo nocturno, compatible con fuga persistente emergente"
      : "reducción del MNF, compatible con reparación de fuga";
  } else if (k.includes("presion") || k.includes("presión")) {
    causa = inc
      ? "aumento de presión, posible cierre de válvula o ajuste de PRV aguas arriba"
      : "caída de presión, posible fuga/rotura o demanda elevada";
  } else if (k.includes("consumo")) {
    causa = inc
      ? "aumento de consumo, posible evento de demanda o fuga"
      : "reducción de consumo, posible interrupción de servicio o efecto estacional";
  } else if (k.includes("rf") || k.includes("cobertura")) {
    causa = inc
      ? "mejora de la cobertura RF"
      : "degradación de la cobertura RF, posible falla de gateway";
  } else if (k.includes("orden")) {
    causa = inc
      ? "aumento de órdenes de inspección, mayor carga operacional"
      : "reducción de órdenes de inspección, descongestión operacional";
  } else {
    causa = inc
      ? "variación al alza atribuible a la dinámica operacional reciente"
      : "variación a la baja atribuible a la dinámica operacional reciente";
  }
  if (sinBase) {
    causa += " (sin base de comparación: el periodo anterior es 0, magnitud porcentual no definida)";
  }
  return causa;
}

/**
 * Construye el {@link KPIInsight} para dos valores consecutivos de un KPI:
 * dirección (incremento/decremento) + **magnitud con signo igual a la variación
 * porcentual relativa al periodo anterior** + causa probable determinista (R5.5).
 *
 * Maneja el caso límite `p0 = 0` (variación porcentual indefinida): la magnitud
 * se reporta como `0` y la causa lo indica de forma explícita. Función pura;
 * asume `p0`/`p1` finitos (lo valida el llamador del contrato).
 */
export function kpiInsight(kpi: string, p0: number, p1: number): KPIInsight {
  const delta = p1 - p0;
  const direccion: "incremento" | "decremento" = delta >= 0 ? "incremento" : "decremento";
  const sinBase = p0 === 0;
  const magnitud_pct = sinBase ? 0 : (delta / Math.abs(p0)) * 100;
  return {
    kpi,
    direccion,
    magnitud_pct,
    causa_probable: causaProbableKPI(kpi, direccion, sinBase),
  };
}

/** Valida que un valor desconocido tenga la forma de una {@link Correlacion}. */
function esCorrelacion(corr: unknown): corr is Correlacion {
  if (!corr || typeof corr !== "object") return false;
  const c = corr as Partial<Correlacion>;
  return (
    typeof c.clave_natural === "string" &&
    !!c.entidad_raiz &&
    typeof c.entidad_raiz === "object" &&
    Array.isArray(c.evidencia) &&
    !!c.cadena_causal &&
    Array.isArray((c.cadena_causal as CadenaCausal).vinculos) &&
    typeof c.confianza === "number" &&
    Array.isArray(c.entidades) &&
    Array.isArray(c.casos)
  );
}

/**
 * Construye un {@link Insight} a partir de una {@link Correlacion}, o
 * `null` si la correlación no está fundamentada. Aplica las reglas de
 * admisión de R5.2/R5.8 **sin fabricar**:
 *
 *   1. forma de correlación válida;
 *   2. **≥1** evidencia de soporte;
 *   3. Cadena_Causal con **≥1** vínculo de Causalidad;
 *   4. confianza **∈ [0, 1]**.
 *
 * Ante cualquier incumplimiento devuelve `null` e invoca el hook de
 * Observabilidad con la causa del descarte (R5.8). Función pura salvo por
 * el efecto de registro explícito a través de `onDiscard`.
 */
export function buildInsightFromCorrelacion(corr: unknown, onDiscard?: ObservabilidadHook): Insight | null {
  if (!esCorrelacion(corr)) {
    onDiscard?.({
      clave_natural: typeof (corr as { clave_natural?: unknown })?.clave_natural === "string"
        ? (corr as { clave_natural: string }).clave_natural
        : "(desconocida)",
      causa: "correlacion_invalida",
      detalle: "La correlación recibida no tiene la forma esperada.",
    });
    return null;
  }

  const descartar = (causa: CausaDescarte, detalle: string): null => {
    onDiscard?.({ clave_natural: corr.clave_natural, causa, detalle });
    return null;
  };

  if (corr.evidencia.length < 1) {
    return descartar("sin_evidencia", "La correlación no dispone de evidencia de soporte.");
  }
  if (corr.cadena_causal.vinculos.length < 1) {
    return descartar("sin_causalidad", "No se pudo construir una Cadena_Causal con al menos un vínculo.");
  }
  if (!Number.isFinite(corr.confianza) || corr.confianza < 0 || corr.confianza > 1) {
    return descartar("confianza_fuera_de_rango", `Confianza ${corr.confianza} fuera del rango [0, 1].`);
  }

  return {
    insight_id: insightIdFor(corr.clave_natural),
    clave_natural: corr.clave_natural,
    narrativa: narrativaMinima(corr),
    evidencia: dedupRefs(corr.evidencia),
    cadena_causal: corr.cadena_causal,
    confianza: corr.confianza,
    entidades: dedupRefs(corr.entidades),
    casos: dedupIds(corr.casos),
  };
}

/**
 * Pipeline puro completo: series → correlaciones → Insights fundamentados,
 * en orden determinista por `insight_id`. Las correlaciones descartadas se
 * notifican vía `onDiscard` (R5.8). Determinista por clave natural (R5.6).
 */
export function buildInsights(
  series: SeriesInsumo,
  params?: ParamsInsights,
  onDiscard?: ObservabilidadHook,
): Insight[] {
  const correlaciones = computeCorrelaciones(series, params);
  const insights: Insight[] = [];
  for (const corr of correlaciones) {
    const insight = buildInsightFromCorrelacion(corr, onDiscard);
    if (insight) insights.push(insight);
  }
  return insights.sort((a, b) => (a.insight_id < b.insight_id ? -1 : a.insight_id > b.insight_id ? 1 : 0));
}

// ---------------------------------------------------------------------------
// Implementación de la interfaz MotorInsights (variante demo)
// ---------------------------------------------------------------------------

/** Opciones de construcción del Motor_Insights demo. */
export interface MotorInsightsOptions {
  /** Proyección del Grafo_Entidades para enlazar entidades/casos (R5.7). */
  grafo?: ProyeccionGrafo;
  /** Hook de Observabilidad para registrar descartes (R5.8). */
  onDiscard?: ObservabilidadHook;
}

/**
 * Motor_Insights client-side. Implementa {@link MotorInsights} del
 * contrato con firmas idénticas a la variante conectada (R17.1, R17.5).
 *
 * El Grafo_Entidades se inyecta por constructor (la firma de `correlate`
 * del contrato no lo recibe como argumento) y se usa para enlazar cada
 * Insight con entidades y Casos del Grafo (R5.7).
 */
export class MotorInsightsLocal implements MotorInsights {
  private readonly grafo?: ProyeccionGrafo;
  private readonly onDiscard: ObservabilidadHook;

  constructor(options: MotorInsightsOptions = {}) {
    this.grafo = options.grafo;
    // Hook por defecto no-op: la integración real (Observabilidad) se
    // inyecta desde el runtime sin acoplar este motor (R5.8).
    this.onDiscard = options.onDiscard ?? (() => {});
  }

  /**
   * Correlaciona ≥2 dominios y construye los Insights fundamentados.
   * Determinista por clave natural (R5.1, R5.6). `series` debe tener la
   * forma de {@link SeriesInsumo}; un insumo inválido degrada a un
   * conjunto vacío sin lanzar a través de la frontera de servicio.
   */
  async correlate(
    series: unknown,
    params: Record<string, unknown>,
    _code_version: string,
  ): Promise<Result<Insight[]>> {
    if (!Array.isArray(series)) {
      return { ok: false, error: "El insumo 'series' debe ser un arreglo de SerieCrossDomain.", degraded: true };
    }
    const insights = buildInsights(series as SeriesInsumo, params as ParamsInsights, this.onDiscard);
    return { ok: true, value: insights };
  }

  /**
   * Construye un Insight desde una correlación o `null` si no está
   * fundamentada, registrando el descarte (R5.2, R5.8).
   */
  buildInsight(corr: unknown): Result<Insight | null> {
    const insight = buildInsightFromCorrelacion(corr, this.onDiscard);
    return { ok: true, value: insight };
  }

  /**
   * Enlaza el Insight con las entidades del Grafo_Entidades y los Casos a
   * los que aplica (R5.7). Cuando hay Grafo disponible, restringe las
   * entidades a las efectivamente proyectadas en él.
   */
  link(insight: Insight): Result<{ entidades: EntidadRef[]; casos: Id[] }> {
    let entidades = dedupRefs(insight.entidades);
    if (this.grafo) {
      const refsGrafo = new Set(this.grafo.nodos.map((n) => refKey(n.entidad_ref)));
      const enGrafo = entidades.filter((e) => refsGrafo.has(refKey(e)));
      // Si ninguna entidad está proyectada aún, se conservan las del Insight
      // (degradación elegante: el Grafo puede no haber materializado el nodo).
      if (enGrafo.length > 0) entidades = enGrafo;
    }
    return { ok: true, value: { entidades, casos: dedupIds(insight.casos) } };
  }

  // -------------------------------------------------------------------------
  // [3.2] Narrativas, evolución de anomalías y KPI intelligence
  //       Implementadas por plantillas deterministas; las firmas del
  //       contrato MotorInsights permanecen intactas (R5.3, R5.4, R5.5).
  // -------------------------------------------------------------------------

  /**
   * Narrativa por plantillas deterministas del Insight (R5.3). Si el Insight
   * ya porta una Narrativa_Operacional bien formada (hallazgo + entidad +
   * ≥1 acción), la respeta; en caso contrario la regenera por plantillas a
   * partir de los dominios inferidos de su Cadena_Causal.
   */
  narrative(insight: Insight): Result<NarrativaOperacional> {
    const n = insight.narrativa;
    const bienFormada =
      !!n &&
      typeof n.hallazgo === "string" &&
      n.hallazgo.length > 0 &&
      !!n.entidad_afectada &&
      Array.isArray(n.acciones_recomendadas) &&
      n.acciones_recomendadas.length >= 1;
    if (bienFormada) return { ok: true, value: n };

    const dominios = dominiosDeCadena(insight.cadena_causal);
    const entidad_afectada = insight.narrativa?.entidad_afectada ?? insight.entidades[0];
    if (!entidad_afectada) {
      return { ok: false, error: "El Insight no tiene entidad afectada para construir la narrativa." };
    }
    return {
      ok: true,
      value: buildNarrative({ dominios, entidad_afectada, confianza: insight.confianza }),
    };
  }

  /**
   * Evolución temporal de una Anomalia persistente a lo largo de `periodos`
   * (≥2), con serie por periodo y Narrativa_Operacional actualizada (R5.4).
   * Determinista por la referencia de la Anomalia (R5.6).
   */
  async anomalyEvolution(anomalia_ref: EntidadRef, periodos: number): Promise<Result<EvolucionAnomalia>> {
    if (!Number.isInteger(periodos) || periodos < 2) {
      return { ok: false, error: `El número de periodos debe ser un entero ≥ 2 (recibido: ${periodos}).` };
    }
    return { ok: true, value: buildAnomalyEvolution(anomalia_ref, periodos) };
  }

  /**
   * KPI intelligence: dirección + magnitud porcentual con signo relativa al
   * periodo anterior + causa probable determinista (R5.5). Maneja `p0 = 0`
   * (variación porcentual indefinida → magnitud 0, causa explícita).
   */
  kpiIntelligence(kpi: string, p0: number, p1: number): Result<KPIInsight> {
    if (!Number.isFinite(p0) || !Number.isFinite(p1)) {
      return { ok: false, error: "Los valores de KPI deben ser numéricos finitos." };
    }
    return { ok: true, value: kpiInsight(kpi, p0, p1) };
  }
}

/** Factoría del Motor_Insights demo para el runtime selector (R17.2). */
export function crearMotorInsightsLocal(options: MotorInsightsOptions = {}): MotorInsightsLocal {
  return new MotorInsightsLocal(options);
}
